import React from "react"
import { makeStyles } from "@material-ui/core/styles"
import InstagramIcon from "@material-ui/icons/Instagram"
import { injectIntl } from "gatsby-plugin-intl"

// Single monochrome Instagram link, used for the doctors' personal accounts.
// Deliberately has no colour of its own: the glyph and the handle both inherit
// `currentColor` from whatever the caller sets, so the same component reads
// correctly on the dark /about bio cards and on the light homepage cards.
// The clinic's own account is NOT rendered through this — it lives in the
// footer (see footer.js) and so already appears on every page.
//
// Props:
//   handle:   "doctor.callum.faris"   (without the @)
//   name:     "Dr Callum Faris"       (used for the accessible label only)
//   showHandle: true                  (icon + @handle; false = icon only)
//   color / hoverColor                (any CSS colour; default: inherit)
//   size:     icon size in px (default 20)

const useStyles = makeStyles({
  link: {
    display: "inline-flex",
    alignItems: "center",
    // 6px vertical padding lifts the tap target past the 24px WCAG minimum
    // even in the icon-only homepage variant.
    padding: "6px 0",
    color: (props) => props.color,
    textDecoration: "none",
    transition: "color 160ms ease, opacity 160ms ease",
    "&:hover": {
      textDecoration: "none",
      color: (props) => props.hoverColor,
      opacity: 0.85,
    },
    "&:focus-visible": {
      outline: "2px solid currentColor",
      outlineOffset: 3,
    },
  },
  icon: {
    fontSize: (props) => props.size,
  },
  handle: {
    marginLeft: 8,
    fontSize: "0.85rem",
    letterSpacing: "0.04em",
    lineHeight: 1,
  },
})

const InstagramLink = ({
  intl,
  handle,
  name,
  showHandle = false,
  color = "inherit",
  hoverColor = "inherit",
  size = 20,
  className,
}) => {
  const classes = useStyles({ color, hoverColor, size })
  if (!handle) return null

  const label = intl.formatMessage({ id: "global.instagramOf" }, { name })

  return (
    <a
      href={`https://www.instagram.com/${handle}/`}
      target="_blank"
      rel="noreferrer"
      aria-label={label}
      className={`${classes.link} ${className || ""}`}
    >
      <InstagramIcon className={classes.icon} />
      {showHandle && <span className={classes.handle}>@{handle}</span>}
    </a>
  )
}

export default injectIntl(InstagramLink)
